import type { AnalyticsSummary } from "@/api/client";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// ~6px per character at font-size 10, plus a gap.
const CHAR_W = 6;
const LABEL_GAP = 8;

function chartLabel(start: string, group: "day" | "week" | "month"): string {
  const [y, m, d] = start.split("-").map(Number) as [number, number, number];
  const month = MONTHS[m - 1];
  return group === "month" ? `${month} ${String(y).slice(-2)}` : `${d} ${month}`;
}

const W = 720;
const H = 200;
const PAD = { top: 8, right: 8, bottom: 28, left: 8 };

export function IssuedChart({ buckets, group }: { buckets: AnalyticsSummary["buckets"]; group: "day" | "week" | "month" }) {
  const n = buckets.length;
  // max(1, …) keeps an all-zero range flat instead of dividing by zero.
  const max = Math.max(1, ...buckets.flatMap((b) => [b.issued, b.reissued]));
  const plotH = H - PAD.top - PAD.bottom;
  const slot = (W - PAD.left - PAD.right) / Math.max(1, n);
  const barW = Math.max(1, (slot * 0.8) / 2);
  const labels = buckets.map((b) => chartLabel(b.start, group));
  const labelW = Math.max(0, ...labels.map((l) => l.length)) * CHAR_W + LABEL_GAP;
  const step = Math.max(1, Math.ceil(labelW / slot));
  const baseline = H - PAD.bottom;

  return (
    <div>
      <ul className="mb-2 flex gap-4 text-xs text-zinc-600">
        <li className="flex items-center gap-1.5">
          <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-sm bg-zinc-900" />
          Issued
        </li>
        <li className="flex items-center gap-1.5">
          <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-sm bg-zinc-400" />
          Re-issued
        </li>
      </ul>
      <svg role="img" aria-label="Issued and re-issued certificates per period" viewBox={`0 0 ${W} ${H}`} className="w-full text-zinc-500">
        <g aria-hidden="true">
          <line x1={PAD.left} x2={W - PAD.right} y1={baseline} y2={baseline} className="stroke-zinc-200" />
          {buckets.map((b, i) => {
            const x = PAD.left + i * slot + slot * 0.1;
            const issuedH = (b.issued / max) * plotH;
            const reissuedH = (b.reissued / max) * plotH;
            return (
              <g key={b.start}>
                <rect x={x} y={baseline - issuedH} width={barW} height={issuedH} className="fill-zinc-900" />
                <rect x={x + barW} y={baseline - reissuedH} width={barW} height={reissuedH} className="fill-zinc-400" />
                {i % step === 0 ? (
                  <text x={x + barW} y={H - 10} textAnchor="middle" fontSize="10" className="fill-zinc-500">
                    {labels[i]}
                  </text>
                ) : null}
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}
